import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { ChancenDynamicClassProduct } from '@chancenwerk/_cms/products/entities/ChancenDynamicClassProduct.js';
import { ChancenDynamicClassProductFilter } from '@chancenwerk/_cms/products/entities/ChancenDynamicClassProductFilter.js';
import type { Product } from '@chancenwerk/_cms/products/entities/Product.js';
import '@chancenwerk/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/design/components/cr-dropdown-search-selection.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { WithId } from 'mongodb';
import { firstValueFrom, switchMap } from 'rxjs';

/**
 * @customElement chancen-component-semantic-product-picker
 */
@customElement('chancen-component-semantic-product-picker')
export class ChancenComponentSemanticProductPicker extends FormField<Product> {
  @property({ attribute: true, type: String }) public placeholder = 'Produkt';
  @property({ attribute: true, reflect: true, type: String }) public 'placeholder-mode' = 'inline-floating';

  @state() private readonly _selectedValue = new RXController(
    this,
    this.value.observable.pipe(
      switchMap(value =>
        ChancenDynamicClassProduct.getChancenDynamicClassProductByClassName(value?._class ?? '')({
          name: 1,
          kuerzel: 1,
        }),
      ),
    ),
  );

  override render() {
    return html`
      <cr-flex>
        <cr-dropdown-search-selection
          flex
          ?disabled=${this.disabled}
          clearable
          placeholder=${this.placeholder}
          placeholder-mode=${this['placeholder-mode']}
          .renderString=${(productDynamicClass: Maybe<ChancenDynamicClassProduct>) => {
            return productDynamicClass?.toString();
          }}
          .search=${(search: Maybe<string>) =>
            firstValueFrom(
              ChancenDynamicClassProduct.subscribeProductsByFilterPaginated(
                0,
                20,
                new ChancenDynamicClassProductFilter({
                  search,
                }),
              )(ChancenDynamicClassProduct.allFields),
            )}
          @value-picked=${(e: ValueEvent<Maybe<ChancenDynamicClassProduct>>) => {
            const constructedProduct = e.detail.value?.generateClass() ?? null;
            this.value.next(constructedProduct);
            const event = new CustomEvent('value-picked', {
              detail: {
                value: constructedProduct,
              },
            });
            this.dispatchEvent(event);
          }}
          .value=${this._selectedValue.value}
        ></cr-dropdown-search-selection>
      </cr-flex>
    `;
  }
}

/**
 * @customElement chancen-component-semantic-product-picker-multi
 */
@customElement('chancen-component-semantic-product-picker-multi')
export class ChancenComponentSemanticProductPickerMulti extends FormField<Maybe<Product[]>> {
  @property({ attribute: true, type: String }) public placeholder = 'Produkt';

  @state() private readonly _selectedValues = new RXController(
    this,
    this.value.observable.pipe(
      switchMap(async values => {
        let res: WithId<ChancenDynamicClassProduct>[] = [];
        for (const value of values ?? []) {
          const n = await ChancenDynamicClassProduct.getChancenDynamicClassProductByClassName(value?._class ?? '')({
            name: 1,
            kuerzel: 1,
          });
          if (n) {
            res.push(n);
          }
        }
        return res;
      }),
    ),
  );

  override render() {
    return html`
      <cr-flex>
        <cr-dropdown-multiselection
          flex
          ?disabled=${this.disabled}
          clearable
          placeholder=${this.placeholder}
          .renderString=${(productDynamicClass: Maybe<ChancenDynamicClassProduct>) => {
            return productDynamicClass?.kuerzel;
          }}
          .renderItem=${(productDynamicClass: Maybe<ChancenDynamicClassProduct>) => {
            return html`
              <cr-flex ${css({ padding: `${this.spacing.sm} ${this.spacing.md}` })}>
                <cr-checkbox
                  disabled
                  boolean-only
                  .value=${this.value.value?.map(p => p._class)?.includes(productDynamicClass!.class)}
                  label=${productDynamicClass ? productDynamicClass.toString() : ''}
                ></cr-checkbox>
              </cr-flex>
            `;
          }}
          .search=${(search: Maybe<string>) =>
            firstValueFrom(
              ChancenDynamicClassProduct.subscribeProductsByFilterPaginated(
                0,
                20,
                new ChancenDynamicClassProductFilter({
                  search,
                }),
              )(ChancenDynamicClassProduct.allFields),
            )}
          @value-picked=${(e: ValueEvent<Maybe<ChancenDynamicClassProduct[]>>) => {
            const constructedProduct = (e.detail.value?.map(v => v.generateClass()).filter(Boolean) ?? []) as Product[];
            this.value.next(constructedProduct);
            const event = new CustomEvent('value-picked', {
              detail: {
                value: constructedProduct,
              },
            });
            this.dispatchEvent(event);
          }}
          .value=${this._selectedValues.value}
        ></cr-dropdown-multiselection>
      </cr-flex>
    `;
  }
}
